/**
 * Custom Opt-Out Form for SunPower Securities Settlement
 *
 * This file extends the base OptOut component with custom transaction tables,
 * modified section titles (A-E), and validateForm hooks.
 */

import { OptOut } from "commonAssets/components/Forms/OptOut";
import { createElement } from "commonAssets/lib/createElement";
import { i18n } from "commonAssets/js/constants";
import { ErrorHandler } from "commonAssets/utils/ErrorHandler";
import { FormFields, FormFieldsLabel } from "./FormFields";

import { findField, addClassToFields, moveFields } from "./common";

/**
 * Custom OptOut class for SunPower Securities Settlement.
 * Extends the base OptOut component with custom transaction rendering,
 * modified section titles, and custom certification content.
 *
 * @extends OptOut
 */
export class CustomOptOut extends OptOut {
    /**
     * Creates a new CustomOptOut instance.
     *
     * @param {Object} options - Configuration options
     * @param {Object} options.config - The pre-parsed claim form configuration
     * @param {boolean} options.enableClaimFields - Whether to enable claim fields
     * @param {boolean} options.enableClaimTransactions - Whether to enable transactions
     * @param {ErrorHandler} options.errorHandler - Error handler instance
     * @param {Function} options.validateForm - Optional validateForm hook
     */
    constructor({
        config,
        enableClaimFields = true,
        enableClaimTransactions = true,
        errorHandler = new ErrorHandler(),
        validateForm = null,
    }) {
        // Pass null for the data URL since we're using pre-parsed config
        super(
            null,
            null,
            null,
            enableClaimFields,
            enableClaimTransactions,
            errorHandler
        );
        this.validateFormHook = validateForm;
        this.formConfig = config;
    }

    /**
     * Initializes the exclusion form from pre-parsed config.
     */
    form() {
        this.init_exclusion(this.formConfig);
    }

    /**
     * Override exclusion_form to customize the opt-out form layout.
     *
     * @param {Object} params - Form parameters from JSON config
     * @returns {HTMLFormElement} The opt-out form element
     */
    exclusion_form(params) {
        const claimFormElems = [
            createElement(
                "h3",
                {
                    class: "text-center text-decoration-underline mb-3",
                },
                [],
                params.form_options.exclusion_form.title
            ),
            createElement(
                "div",
                {
                    class: "alert alert-info text-dark text-center fw-semibold mb-3",
                    id: "exclusion_form_top_notice",
                },
                [],
                i18n.getTranslation("exclusion_form_top_notice")
            ),
            createElement(
                "div",
                {
                    class: "text-center mb-3",
                },
                [],
                params.form_options.exclusion_form.body
            ),
            createElement(
                "div",
                {
                    class: "bg-light text-dark border rounded p-3 mb-5",
                },
                [
                    this.claim_contact_information(params),
                    this.claim_fieldset(params),
                ]
            ),
        ];

        // Add transactions if enabled
        if (this.enableClaimTransactions) {
            claimFormElems.push(this.claim_transactions(params));
        }

        // Add certification section
        claimFormElems.push(
            this.claim_certification(params.form_options.exclusion_form)
        );

        const form = createElement(
            "form",
            {
                class: "needs-validation",
                id: "form",
                method: "post",
            },
            claimFormElems
        );

        this.formElement = form;
        return form;
    }

    /**
     * Override claim_contact_information to use custom title from config.
     *
     * @param {Object} params - Form parameters
     * @returns {HTMLElement} Contact information section
     */
    claim_contact_information(params) {
        // Get contact info config from exclusion_form or fall back to claim_form
        const contactConfig =
            params.form_options.exclusion_form?.contact_information ||
            params.form_options.claim_form?.contact_information ||
            {};

        const title =
            contactConfig.title || "A. SETTLEMENT CLASS MEMBER INFORMATION";
        const body =
            contactConfig.body ||
            '<div class="text-danger fw-bold">* Required Fields</div>';

        const div = createElement(
            "div",
            {
                class: "bg-light text-dark border rounded p-3 mb-5",
                id: "contact-information",
            },
            [
                createElement(
                    "div",
                    { class: "h3 mb-3 formTitle", id: "contactInfoTitle" },
                    [],
                    title
                ),
                createElement(
                    "div",
                    { class: "mb-3 formBody", id: "contactInfoBody" },
                    [],
                    body
                ),
                this.claim_contact_fields(params),
            ]
        );
        return div;
    }

    /**
     * Override claim_contact_fields to properly render intl-phone-input custom elements.
     *
     * @param {Object} params - Form parameters containing contact_information
     * @returns {HTMLElement} Container with rendered contact fields
     */
    claim_contact_fields(params) {
        const parent = createElement("div", {
            class: "row row-cols-1 row-cols-md-2",
        });
        let fieldID = 0;
        params.contact_information.forEach((field) => {
            let validation;
            const d_none =
                field.name === "province" || field.name === "postal_code"
                    ? " d-none"
                    : "";

            const input = FormFields(
                field,
                "",
                fieldID,
                null,
                validation,
                parent
            );
            input.setAttribute("data-contact-label", field.label);
            input.removeAttribute("data-label");

            const div = createElement(
                "div",
                {
                    class: "col mb-3 divContactField" + d_none,
                    id: "divContactField_" + fieldID,
                },
                [
                    createElement(
                        "label",
                        { for: field.name },
                        [],
                        `${field.label}${field.required ? "*" : ""} <span class="error"></span>`
                    ),
                    input,
                ]
            );
            parent.appendChild(div);
            fieldID++;
        });
        return parent;
    }

    /**
     * Override claim_fields to properly render intl-phone-input custom elements.
     *
     * @param {Object} params - Form parameters containing claim_fields
     * @returns {HTMLElement} Container with rendered form fields
     */
    claim_fields(params) {
        const container = createElement("div");

        let fieldID = 0;
        params.claim_fields.forEach((field) => {
            const className = field.className ? field.className : ``;

            const div = createElement("div", {
                class: field.parentClass ? field.parentClass : "mb-3 _wrapper",
                "data-field-id":
                    "_" + (field.name ? field.name : "_wrapper_" + fieldID),
            });
            const element = FormFields(field, className, fieldID);
            FormFieldsLabel(field, fieldID, div);

            // Set data-label if it exists
            if (field.dataLabel) {
                element.setAttribute("data-label", field.dataLabel);
            }

            div.append(element);
            container.appendChild(div);
            fieldID++;
        });

        return container;
    }

    /**
     * Override claim_transactions to render grouped transactions with custom titles.
     * Groups tables by part_b, part_c, part_d and uses titles from config.
     *
     * @param {Object} params - Form parameters containing claim_transactions
     * @returns {HTMLElement} Transactions container element
     */
    claim_transactions(params) {
        if (params.claim_transactions === undefined) {
            return createElement("div", { class: "d-none" });
        }
        if (params.claim_transactions.length === 0) {
            return createElement("div", { class: "d-none" });
        }

        // Main container for all transaction groups
        const mainContainer = createElement("div", {
            id: "transactions",
        });

        // Get custom titles from exclusion_form.transactions config, with fallbacks
        const exclusionTransactions =
            params.form_options.exclusion_form?.transactions || {};
        const customTitles = {
            part_b:
                exclusionTransactions.part_b_title ||
                "B. TRANSACTIONS IN SUNPOWER COMMON STOCK",
            part_c:
                exclusionTransactions.part_c_title ||
                "C. TRANSACTIONS IN SUNPOWER CALL OPTIONS",
            part_d:
                exclusionTransactions.part_d_title ||
                "D. TRANSACTIONS IN SUNPOWER PUT OPTIONS",
        };

        // Group tables by part prefix (part_b_, part_c_, part_d_)
        const claim_transactions = params.claim_transactions;
        const groups = {};
        claim_transactions.forEach((table) => {
            const partMatch = table.id?.match(/^(part_[a-z])_/);
            const partKey = partMatch ? partMatch[1] : "part_b";
            if (!groups[partKey]) groups[partKey] = [];
            groups[partKey].push(table);
        });

        // Create container for each group
        let tableID = 0;
        Object.keys(groups)
            .sort()
            .forEach((partKey) => {
                const groupContainer = createElement(
                    "div",
                    {
                        class: "bg-light text-dark border rounded p-3 mb-5",
                        id: `transactions_${partKey}`,
                    },
                    [
                        createElement(
                            "div",
                            { class: "h3 mb-3 formTitle" },
                            [],
                            customTitles[partKey] ||
                                params.form_options.transactions[
                                    `${partKey}_title`
                                ]
                        ),
                        // No body/description for opt-out form
                    ]
                );

                // Render each table in this group
                let isFirstInGroup = true;
                let tableIndexInPart = 0;
                const partIndex = partKey.charCodeAt(5) - 96;

                groups[partKey].forEach((table) => {
                    tableID++;
                    tableIndexInPart++;
                    const itemIdOffset =
                        partIndex * 1000 + tableIndexInPart * 100;

                    // Add separator line between tables (not before the first one)
                    if (!isFirstInGroup) {
                        const separator = createElement("hr", {
                            class: "my-4",
                        });
                        groupContainer.appendChild(separator);
                    }
                    isFirstInGroup = false;

                    // Title as h2 above the table (no description)
                    const tableTitle = createElement(
                        "h2",
                        { class: "h5 fw-bold mb-2" },
                        [],
                        table.name
                    );
                    groupContainer.appendChild(tableTitle);
                    // Description as paragraph below the title
                    if (table.description) {
                        const tableDescription = createElement(
                            "p",
                            { class: "mb-3 text-muted" },
                            [],
                            table.description
                        );
                        groupContainer.appendChild(tableDescription);
                    }
                    // Create responsive table container
                    const divResponsiveTable = createElement("div", {
                        class: "table-responsive mb-3",
                        id: "table_responsive_" + tableID,
                    });

                    // Create table element
                    const tableTransaction = createElement(
                        "table",
                        {
                            class: "table table-hover transactions",
                            id: "transactions_" + tableID,
                            "data-table-name": table.name,
                            "data-table-id": tableID,
                        },
                        []
                    );
                    divResponsiveTable.appendChild(tableTransaction);

                    // Create table header
                    const tableTHEAD = createElement("thead", {
                        class: "table-dark text-light",
                    });
                    tableTransaction.appendChild(tableTHEAD);

                    const theadTR = createElement("tr");
                    tableTHEAD.appendChild(theadTR);

                    // Add column headers
                    table.columns.forEach((col) => {
                        const column = createElement(
                            "th",
                            { scope: "col" },
                            [],
                            col.title
                        );
                        theadTR.appendChild(column);

                        if (Boolean(col.body)) {
                            const helper = createElement(
                                "span",
                                {
                                    class: "d-block fw-normal",
                                    style: "font-size:13px;",
                                },
                                [],
                                col.body
                            );
                            column.appendChild(helper);
                        }
                    });

                    // Create table body
                    const tableTBODY = createElement("tbody");
                    tableTransaction.appendChild(tableTBODY);

                    // Add initial row
                    const countRow = 1;
                    this.claim_transaction_row(
                        table,
                        tableTBODY,
                        tableID,
                        countRow,
                        itemIdOffset
                    );

                    // Add row controls
                    const addButtonLabel = table.addButtonLabel || table.name;
                    const canChangeRows = table.canChangeRows !== false;
                    const add_row = createElement(
                        "div",
                        {
                            class: canChangeRows
                                ? "text-center"
                                : "text-center d-none",
                        },
                        [
                            createElement(
                                "a",
                                {
                                    class: "btn btn-primary m-1",
                                    id: "add_transactions_" + tableID,
                                    role: "button",
                                    tabindex: "0",
                                    "aria-label":
                                        i18n.getTranslation(
                                            "transaction_add_row"
                                        ) + table.name,
                                    tableID: tableID,
                                },
                                [],
                                `<i class="bi bi-plus-circle" tableID="${tableID}"></i> ${addButtonLabel}`
                            ),
                            createElement(
                                "a",
                                {
                                    class: "btn btn-danger m-1 disabled",
                                    id: "remove_transactions_" + tableID,
                                    role: "button",
                                    tabindex: "0",
                                    "aria-label": i18n.getTranslation(
                                        "transaction_delete_row"
                                    ),
                                    tableID: tableID,
                                },
                                [],
                                `<i class="bi bi-dash-circle" tableID="${tableID}"></i> ${i18n.getTranslation("transaction_delete_row")}`
                            ),
                        ]
                    );

                    if (canChangeRows) {
                        this.claim_transaction_row_buttons_events(
                            add_row,
                            table,
                            tableTBODY,
                            tableID,
                            countRow,
                            itemIdOffset
                        );
                    }

                    groupContainer.appendChild(divResponsiveTable);
                    groupContainer.appendChild(add_row);
                });

                mainContainer.appendChild(groupContainer);
            });

        return mainContainer;
    }

    /**
     * Add a transaction row with custom item-id handling.
     *
     * @param {Object} table - Table configuration
     * @param {HTMLElement} tableTBODY - Table body element
     * @param {string} tableID - Table identifier
     * @param {number} countRow - Row count
     * @param {number} itemIdOffset - Offset for item IDs
     * @returns {Object} Object containing tableTBODY and fieldsRow
     */
    claim_transaction_row(table, tableTBODY, tableID, countRow, itemIdOffset) {
        const fieldsRow = createElement("tr", {
            class: "transaction-row",
            id: "transaction_row_" + tableID + "_" + countRow,
        });
        tableTBODY.appendChild(fieldsRow);

        let fieldID = 0;
        table.fields.forEach((field) => {
            fieldID++;
            const originalFieldName = field.name;

            const td = createElement("td", {
                class: "transaction " + originalFieldName,
            });
            const element = createElement("div");
            const validation = field.validation;

            const formInput = FormFields(
                field,
                "",
                fieldID,
                element,
                validation,
                fieldsRow
            );

            field.name = originalFieldName + "__" + countRow;
            FormFieldsLabel(field, fieldID, element);

            formInput.id = formInput.id + "__" + countRow;
            formInput.setAttribute("item-id", itemIdOffset + countRow);
            formInput.setAttribute("data-table-id", table.id);
            formInput.setAttribute("data-label", field.label);

            element.appendChild(formInput);
            td.appendChild(element);
            fieldsRow.appendChild(td);

            field.name = originalFieldName;
        });
        return { tableTBODY, fieldsRow };
    }

    /**
     * Add event listeners for add/remove row buttons.
     *
     * @param {HTMLElement} add_row - Container with add/remove buttons
     * @param {Object} table - Table configuration
     * @param {HTMLElement} tableTBODY - Table body element
     * @param {string} tableID - Table identifier
     * @param {number} countRow - Initial row count
     * @param {number} itemIdOffset - Offset for item IDs
     */
    claim_transaction_row_buttons_events(
        add_row,
        table,
        tableTBODY,
        tableID,
        countRow,
        itemIdOffset
    ) {
        add_row.firstChild.addEventListener("click", (event) => {
            countRow++;
            const table_id = event.target.attributes.tableID.value;
            this.claim_transaction_row(
                table,
                tableTBODY,
                table_id,
                countRow,
                itemIdOffset
            );
            if (countRow > 1) {
                document
                    .querySelector(`#remove_transactions_${table_id}`)
                    .classList.remove("disabled");
            }
        });

        add_row.lastChild.addEventListener("click", (event) => {
            countRow--;
            const table_id = event.target.attributes.tableID.value;
            const tableEl = document.querySelector(`#transactions_${table_id}`);
            const table_rows = tableEl.rows.length;
            const table_tbody = tableEl.querySelector("tbody");
            table_tbody.removeChild(table_tbody.lastChild);
            if (table_rows === 3) {
                document
                    .querySelector(`#remove_transactions_${table_id}`)
                    .classList.add("disabled");
            }
        });
    }

    /**
     * Override claim_certification with title and description from config.
     *
     * @param {Object} params - Certification parameters (exclusion_form config)
     * @returns {HTMLElement} Certification section element
     */
    claim_certification(params) {
        const today = new Date().toLocaleString();
        const _ag_uuid = document.cookie
            .split("; ")
            .find((row) => row.startsWith("_ag_UUID="))
            ?.split("=")[1];
        const _ag_referrer = document.cookie
            .split("; ")
            .find((row) => row.startsWith("_ag_URL="))
            ?.split("=")[1];

        // Get title and body from config with fallbacks
        const certificationTitle =
            params.certification?.attestation?.title || "E. SIGNATURE";
        const certificationBody = params.certification?.attestation?.body || "";
        const fieldset = createElement(
            "div",
            {
                class: "bg-light text-dark border rounded p-3 mb-5",
                id: "certification",
            },
            [
                createElement(
                    "div",
                    { class: "h3 mb-3 formTitle" },
                    [],
                    certificationTitle
                ),
                createElement("div", { class: "mb-3" }, [], certificationBody),
                createElement(
                    "div",
                    {
                        class: "alert alert-info border-info bg-info-subtle text-info-emphasis mb-4 px-4 py-3",
                        role: "region",
                        "aria-labelledby": "ueta-notice-heading",
                        "aria-describedby": "ueta-notice-text",
                    },
                    [
                        createElement(
                            "div",
                            { class: "d-flex align-items-start gap-3" },
                            [
                                createElement("i", {
                                    class: "bi bi-info-circle-fill text-info fs-5 flex-shrink-0 mt-1",
                                    "aria-hidden": "true",
                                }),
                                createElement("div", { class: "flex-grow-1" }, [
                                    createElement(
                                        "h6",
                                        {
                                            class: "fw-bold mb-2 text-info-emphasis",
                                            id: "ueta-notice-heading",
                                        },
                                        [],
                                        i18n.getTranslation(
                                            "signature_ueta_title"
                                        )
                                    ),
                                    createElement(
                                        "p",
                                        {
                                            class: "mb-0 small lh-sm",
                                            id: "ueta-notice-text",
                                        },
                                        [],
                                        i18n.getTranslation(
                                            "signature_ueta_compliant"
                                        )
                                    ),
                                ]),
                            ]
                        ),
                    ]
                ),
                createElement("div", { class: "row" }, [
                    createElement("div", { class: "col-md-5" }, [
                        createElement("input", {
                            type: "text",
                            name: "signature",
                            class: "form-control",
                            id: "signature",
                            "data-validate": "required",
                            "data-contact-label":
                                i18n.getTranslation("signature"),
                            placeholder: i18n.getTranslation(
                                "signature_placholder"
                            ),
                            required: "",
                        }),
                        createElement(
                            "label",
                            { for: "signature" },
                            [],
                            `${i18n.getTranslation("signature")}* <span class="error"></span>`
                        ),
                    ]),
                    createElement("div", { class: "col-md-5" }, [
                        createElement("input", {
                            type: "text",
                            name: "date",
                            class: "form-control",
                            id: "date",
                            "data-validate": "required",
                            "data-contact-label": "Date",
                            disabled: "disabled",
                            value: today,
                        }),
                        createElement(
                            "label",
                            { for: "date" },
                            [],
                            `${i18n.getTranslation("date")} <span class="error"></span>`
                        ),
                    ]),
                    createElement("div", { class: "col-md-2" }, [
                        createElement("div", { class: "d-grid" }, [
                            createElement("input", {
                                type: "hidden",
                                name: "ag_UUID",
                                id: "ag_UUID",
                                value: _ag_uuid,
                            }),
                            createElement("input", {
                                type: "hidden",
                                name: "referer_url",
                                id: "referer_url",
                                value: _ag_referrer,
                            }),
                            createElement("input", {
                                type: "submit",
                                class: "btn btn-primary",
                                id: "submit-claim",
                                value: i18n.getTranslation("submit_btn"),
                            }),
                        ]),
                    ]),
                ]),
            ]
        );
        return fieldset;
    }

    /**
     * Override init_exclusion_fields to trigger validateForm hook.
     *
     * @param {Object} params - Optional parameters
     */
    init_exclusion_fields(params) {
        super.init_exclusion_fields(params);

        // Trigger validateForm hook if provided
        if (this.validateFormHook) {
            this.validateFormHook({ event: "init_exclusion", ctx: this });
        }
    }

    /**
     * Creates a new CustomOptOut instance.
     *
     * @param {Object} options - Configuration options
     * @param {Object} options.config - The pre-parsed claim form configuration
     * @param {boolean} options.enableClaimFields - Whether to enable claim fields
     * @param {boolean} options.enableClaimTransactions - Whether to enable transactions
     * @param {ErrorHandler} options.errorHandler - Error handler instance
     * @param {Function} options.validateForm - Optional validateForm hook
     * @returns {CustomOptOut} New CustomOptOut instance
     */
    static createInstance({
        config,
        enableClaimFields = true,
        enableClaimTransactions = true,
        errorHandler = new ErrorHandler(),
        validateForm = null,
    }) {
        return new CustomOptOut({
            config,
            enableClaimFields,
            enableClaimTransactions,
            errorHandler,
            validateForm,
        });
    }
}

/**
 * ValidateForm hook for opt-out form.
 * Called during form initialization and processing.
 *
 * @param {Object} options - Options object
 * @param {string|Event} options.event - The event type or event object
 * @param {CustomOptOut} options.ctx - The CustomOptOut context
 */

export function validateForm({ event }) {
    if (event === "init_exclusion") {

        console.log("[OptOut Form Initialized]");
        // Organize contact section field order
        organizeContactSection();
    }

    if (event === "process_exclusion") {

        console.log("[Processing OptOut Form]");
        // Add any processing validation here
    }
}

export function organizeContactSection() {
    const fieldset = findField("last_name").parentElement;

    // Define field groups and their reference points
    const fieldGroups = [
        {
            fields: [
                "representative_or_custodian_name",
                "entity_name",
                "co_beneficial_owner_name",
                "beneficial_owner_name",
            ],
            referenceField: "last_name",
        },
    ];

    addClassToFields(fieldGroups[0].fields, "w-100");

    // Process each field group
    fieldGroups.forEach(({ fields, referenceField }) => {
        const referenceNode = findField(referenceField);
        moveFields(fields, referenceNode, fieldset);
    });
}
